// ---------------------------------------------------------------------------
// EvaluationTemplate — workspace-scoped, versioned configuration document
// that names the agent under test, the graders to apply, the data + query
// sets, and an optional sweep. Mirrors the EvaluationTemplate section of the
// Workflow Service API spec; lives under
//   /workspaces/{workspaceName}/evaluation/evaluation-templates
//
// v1 surfaces only the identity + audit shell (name / displayName /
// description / isDraft). The deeper authoring fields (tasks, dataSets,
// querySets, concurrency, variables) are persisted server-side but not yet
// modelled here — see follow-up work.
// ---------------------------------------------------------------------------

import type { SystemProperties } from './common.js';

export const EVALUATION_TEMPLATE_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;
export const EVALUATION_TEMPLATE_NAME_MAX_LENGTH = 63;
export const EVALUATION_TEMPLATE_DISPLAY_NAME_MAX_LENGTH = 100;
export const EVALUATION_TEMPLATE_DESCRIPTION_MAX_LENGTH = 500;

export interface EvaluationTemplate {
  name: string;
  displayName?: string;
  description?: string;
  isDraft: boolean;
  systemProperties: SystemProperties;
}

export interface EvaluationTemplateVersion extends EvaluationTemplate {
  version: number;
}

export interface UpsertEvaluationTemplateBody {
  displayName?: string;
  description?: string;
}

// The server's PATCH endpoint (PatchEvaluationTemplateRequest.cs) accepts
// more than the simple Upsert body — non-null list fields fully replace
// the stored list, and concurrency/variables can be set independently of
// the identity metadata. Untyped here (`unknown[]`/`unknown`) because the
// authoring page that builds these payloads already constructs them
// via a cast; modelling the nested specs properly is a follow-up.
export interface PatchEvaluationTemplateBody {
  displayName?: string;
  description?: string;
  dataSets?: unknown[];
  querySets?: unknown[];
  tasks?: unknown[];
  concurrency?: unknown;
  variables?: unknown;
}

export function isValidEvaluationTemplateName(name: string): boolean {
  return (
    name.length > 0 &&
    name.length <= EVALUATION_TEMPLATE_NAME_MAX_LENGTH &&
    EVALUATION_TEMPLATE_NAME_PATTERN.test(name)
  );
}
